/**
 * CampaignDetailDrawer - Drawer component for confirmed campaign details
 * Shows full campaign info + generation path (snapshot)
 * 
 * Key Features:
 * - Shows how the campaign was generated (Steps 0-3 path)
 * - Independent delete (doesn't affect original Plan)
 */

import React, { useState } from 'react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  X, Calendar, Target, TrendingUp, Globe, ExternalLink, 
  FileText, Megaphone, Layers, BarChart3, AlertTriangle,
  ClipboardCheck, Star, Copy, Check, Users, Sparkles, 
  Building2, DollarSign, Clock, Shield, Trash2, ChevronDown, ChevronUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Channel info
const CHANNEL_INFO = {
  search_paid: { label: 'Search', icon: '🔍' },
  meta_paid: { label: 'Meta', icon: '📱' },
  linkedin_ads: { label: 'LinkedIn', icon: '💼' },
  retargeting: { label: 'Retargeting', icon: '🔄' },
  email_lifecycle: { label: 'Email', icon: '📧' },
  partnerships: { label: 'Partners', icon: '🤝' }
};

// Stage colors
const STAGE_COLORS = {
  decision: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  consideration: 'bg-blue-100 text-blue-700 border-blue-200',
  retargeting: 'bg-orange-100 text-orange-700 border-orange-200',
  awareness: 'bg-violet-100 text-violet-700 border-violet-200',
  retention: 'bg-teal-100 text-teal-700 border-teal-200'
};

// Tag colors
const TAG_DOT_COLORS = {
  core: 'bg-emerald-500',
  adjacent: 'bg-amber-500',
  edge: 'bg-slate-400'
};

export default function CampaignDetailDrawer({ campaign, isOpen, onClose, onDeleteCampaign }) {
  const navigate = useNavigate();
  const [copiedLPC, setCopiedLPC] = useState(false);
  const [showGenerationPath, setShowGenerationPath] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-700 bg-emerald-50 border-emerald-200';
    if (score >= 60) return 'text-amber-700 bg-amber-50 border-amber-200';
    return 'text-slate-600 bg-slate-50 border-slate-200';
  };

  if (!isOpen || !campaign) return null;

  const channelInfo = CHANNEL_INFO[campaign.channel] || { label: campaign.channel, icon: '📢' };
  const payload = campaign.payload || {};
  const messaging = payload.messaging_angle || {};
  const targeting = payload.targeting_unit || {};
  const landing = payload.landing || {};
  const evidence = payload.evidence || [];
  const dependencies = payload.dependencies || [];
  const validation = payload.validation || {};
  const validationChecks = validation.checks || [];
  const hooks = messaging.example_hooks || [];
  const score = campaign.recommendation_score || campaign.score || 0;
  const isTopPick = score >= 75;
  
  // Generation path snapshots
  const step0 = campaign.step0_goal_offer || {};
  const step1 = campaign.step1_personas || {};
  const step2 = campaign.step2_segments || {};
  const step3 = campaign.step3_constraints || {};
  const step1Personas = step1.selected_personas || [];
  const step2Segments = step2.selected_segments || [];
  const hasGenerationPath = step0.primary_goal || step1Personas.length > 0 || 
                           step2Segments.length > 0 || step3.company_stage;
  const isMissingSnapshot = step0.missing || step1.missing || step2.missing || step3.missing;
  
  // Get campaign ID for deletion
  const campaignId = campaign.bucket_campaign_id || campaign.confirmed_campaign_id;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-xl z-50 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b bg-slate-50 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">{channelInfo.icon}</span>
                <h2 className="font-semibold text-lg text-slate-800 line-clamp-2">
                  {campaign.name}
                </h2>
                {isTopPick && (
                  <Star className="w-4 h-4 text-amber-500 fill-amber-400 shrink-0" />
                )}
              </div>
              
              {/* Plan info */}
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <FileText className="w-3.5 h-3.5" />
                <span>From: {campaign.plan_name}</span>
                {campaign.asset_name && (
                  <>
                    <span className="text-slate-300">|</span>
                    <Globe className="w-3.5 h-3.5" />
                    <span>{campaign.asset_name}</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {onDeleteCampaign && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => onDeleteCampaign(campaignId, campaign?.name || 'this campaign')}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  title="Delete from Bucket List (doesn't affect original Plan)"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* Key Metrics */}
          <div className="flex items-center gap-3 flex-wrap">
            <Badge variant="outline" className={`${STAGE_COLORS[campaign.stage] || ''}`}>
              {campaign.stage}
            </Badge>
            <Badge variant="outline" className={`${getScoreColor(score)}`}>
              <TrendingUp className="w-3 h-3 mr-1" />
              Score: {score}
            </Badge>
            {campaign.tag && (
              <span className="inline-flex items-center gap-1 text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded-full border border-slate-200">
                <span className={`w-2 h-2 rounded-full ${TAG_DOT_COLORS[campaign.tag] || 'bg-slate-400'}`}></span>
                {campaign.tag}
              </span>
            )}
            {(campaign.landing_type || landing.type) && (
              <Badge variant="outline" className="text-xs">
                {(campaign.landing_type || landing.type) === 'personalize_existing' ? '✨ Personalize' : '📄 New Page'}
              </Badge>
            )}
          </div>
          
          {/* Independent Snapshot Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
            <div className="flex items-center gap-2 text-blue-700 font-medium mb-1">
              <Sparkles className="w-4 h-4" />
              Independent Snapshot
            </div>
            <p className="text-blue-600 text-xs">
              This is a confirmed copy. Deleting it won't affect your original Plan.
            </p>
          </div>
          
          {/* Generation Path (Collapsible) */}
          {hasGenerationPath && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
                onClick={() => setShowGenerationPath(!showGenerationPath)}
              >
                <span className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  How This Was Generated
                </span>
                {showGenerationPath ? (
                  <ChevronUp className="w-4 h-4 text-slate-500" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                )}
              </button>
              
              {showGenerationPath && (
                <div className="p-4 space-y-4 bg-white">
                  {isMissingSnapshot && (
                    <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
                      ⚠️ This was confirmed before snapshot feature. Some path data may be missing.
                    </div>
                  )}
                  
                  {/* Step 0: Goal/Offer */}
                  {(step0.primary_goal || step0.offer) && (
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-1">
                        <span className="w-4 h-4 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-[10px] font-bold">0</span>
                        Goal & Offer
                      </div>
                      <div className="text-sm text-slate-700 bg-slate-50 rounded p-2">
                        {step0.primary_goal && <p><span className="text-slate-500">Goal:</span> {step0.primary_goal}</p>}
                        {step0.offer && <p><span className="text-slate-500">Offer:</span> {step0.offer}</p>}
                      </div>
                    </div>
                  )}
                  
                  {/* Step 1: Personas */}
                  {step1Personas.length > 0 && (
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-1">
                        <span className="w-4 h-4 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-[10px] font-bold">1</span>
                        Personas
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {step1Personas.map((p, i) => (
                          <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {p.name || p.persona_name || `Persona ${i + 1}`}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Step 2: Segments */}
                  {step2Segments.length > 0 && (
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-1">
                        <span className="w-4 h-4 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-[10px] font-bold">2</span>
                        Segments
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {step2Segments.map((s, i) => (
                          <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full flex items-center gap-1">
                            <Target className="w-3 h-3" />
                            {s.segment_name || s.name || `Segment ${i + 1}`}
                          </span>
                        ))}
                      </div>
                      {step2.focus && (
                        <p className="text-xs text-slate-500 mt-1">Focus: {step2.focus}</p>
                      )}
                    </div>
                  )}
                  
                  {/* Step 3: Constraints */}
                  {step3.company_stage && (
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-1">
                        <span className="w-4 h-4 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-[10px] font-bold">3</span>
                        Generation Settings
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-slate-50 rounded p-2 flex items-center gap-2">
                          <Building2 className="w-3 h-3 text-slate-400" />
                          <span className="text-slate-600">{step3.company_stage}</span>
                        </div>
                        {step3.budget && (
                          <div className="bg-slate-50 rounded p-2 flex items-center gap-2">
                            <DollarSign className="w-3 h-3 text-slate-400" />
                            <span className="text-slate-600">{step3.budget}</span>
                          </div>
                        )}
                        {step3.time_horizon && (
                          <div className="bg-slate-50 rounded p-2 flex items-center gap-2">
                            <Clock className="w-3 h-3 text-slate-400" />
                            <span className="text-slate-600">{step3.time_horizon}</span>
                          </div>
                        )}
                        {step3.stance && (
                          <div className="bg-slate-50 rounded p-2 flex items-center gap-2">
                            <Shield className="w-3 h-3 text-slate-400" />
                            <span className="text-slate-600">{step3.stance}</span>
                          </div>
                        )}
                      </div>
                      {step3.channels_selected && step3.channels_selected !== 'auto' && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(Array.isArray(step3.channels_selected) ? step3.channels_selected : []).map((ch, i) => (
                            <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                              {CHANNEL_INFO[ch]?.label || ch}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Why Recommended */}
          {payload.why_recommended && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-sm text-purple-800">{payload.why_recommended}</p>
            </div>
          )}
          
          {/* Segment */}
          {campaign.segment_name && (
            <div className="flex items-center gap-2 text-sm">
              <Target className="w-4 h-4 text-slate-500" />
              <span className="text-slate-600">Segment:</span>
              <span className="font-medium text-slate-800">{campaign.segment_name}</span>
            </div>
          )}
          
          {/* Created Date */}
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Calendar className="w-3 h-3" />
            Confirmed: {formatDate(campaign.created_at)}
          </div>
          
          {/* Open Original Plan Button */}
          {campaign.plan_id && (
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate(`/plans/${campaign.plan_id}`)}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Original Plan
            </Button>
          )}
          
          {/* Divider */}
          <div className="border-t border-slate-200 pt-4">
            <h3 className="font-semibold text-sm text-slate-700 mb-3">Campaign Details</h3>
          </div>
          
          {/* Messaging Angle */}
          {messaging.angle_name && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-600 uppercase tracking-wide">
                <Megaphone className="w-3.5 h-3.5" />
                Messaging Angle
              </div>
              <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-slate-700">{messaging.angle_name}</span>
                  {messaging.cta && (
                    <span className="text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded">
                      CTA: {messaging.cta}
                    </span>
                  )}
                </div>
                {messaging.promise && (
                  <p className="text-sm text-slate-600">{messaging.promise}</p>
                )}
                {hooks.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {hooks.map((hook, i) => (
                      <span key={i} className="text-xs text-slate-500 bg-white px-2 py-1 rounded border border-slate-200">
                        "{hook}"
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Targeting */}
          {targeting.type && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-600 uppercase tracking-wide">
                <Target className="w-3.5 h-3.5" />
                Targeting
              </div>
              <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-sm">
                <span className="text-xs text-slate-500 bg-white px-2 py-0.5 rounded border border-slate-200">
                  {targeting.type?.replace(/_/g, ' ')}
                </span>
                {targeting.keyword_themes?.length > 0 && (
                  <p className="text-slate-600 pt-1">
                    <span className="text-slate-500">Keywords:</span> {targeting.keyword_themes.join(', ')}
                  </p>
                )}
                {targeting.interest_themes?.length > 0 && (
                  <p className="text-slate-600">
                    <span className="text-slate-500">Interests:</span> {targeting.interest_themes.join(', ')}
                  </p>
                )}
                {targeting.job_title_themes?.length > 0 && (
                  <p className="text-slate-600">
                    <span className="text-slate-500">Job Titles:</span> {targeting.job_title_themes.join(', ')}
                  </p>
                )}
              </div>
            </div>
          )}
          
          {/* Landing */}
          {landing.type && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-600 uppercase tracking-wide">
                <Layers className="w-3.5 h-3.5" />
                Landing
              </div>
              <div className="bg-slate-50 rounded-lg p-3 space-y-2 text-sm">
                {landing.type === 'personalize_existing' ? (
                  <>
                    <p className="text-xs text-slate-500">Personalize sections:</p>
                    <div className="flex flex-wrap gap-1">
                      {(landing.personalize_sections || []).map((s, i) => (
                        <span key={i} className="text-xs text-slate-600 bg-white px-2 py-0.5 rounded border border-slate-200">{s}</span>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-slate-500">New page outline:</p>
                    <div className="space-y-1">
                      {(landing.new_page_outline || []).map((item, i) => (
                        <p key={i} className="text-xs text-slate-600">
                          <span className="font-medium">{item.section}:</span> {item.notes}
                        </p>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          
          {/* Landing Page Context */}
          {(payload.landing_page_context || campaign.landing_page_context) && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-medium text-teal-700 uppercase tracking-wide">
                <Layers className="w-3.5 h-3.5" />
                Landing Page Context
              </div>
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 relative">
                <p className="text-sm text-teal-800 pr-8 leading-relaxed">
                  {payload.landing_page_context || campaign.landing_page_context}
                </p>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(payload.landing_page_context || campaign.landing_page_context);
                      setCopiedLPC(true);
                      setTimeout(() => setCopiedLPC(false), 2000);
                    } catch (err) {
                      console.error('Copy failed:', err);
                    }
                  }}
                  className="absolute top-2 right-2 p-1.5 rounded-md bg-teal-100 hover:bg-teal-200 text-teal-600 transition-colors"
                  title="Copy to clipboard"
                >
                  {copiedLPC ? (
                    <Check className="w-4 h-4 text-teal-700" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
              <p className="text-xs text-slate-400 italic">
                Use this context for landing page personalization
              </p>
            </div>
          )}
          
          {/* Evidence */}
          {evidence.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-600 uppercase tracking-wide">
                <BarChart3 className="w-3.5 h-3.5" />
                Evidence ({evidence.length})
              </div>
              <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                {evidence.map((ev, i) => (
                  <div key={i} className="text-xs flex gap-2 items-start">
                    <span className="text-slate-500 bg-white px-1.5 py-0.5 rounded border border-slate-200 shrink-0">{ev.source}</span>
                    <span className="text-slate-600">{ev.detail}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Dependencies */}
          {dependencies.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-600 uppercase tracking-wide">
                <AlertTriangle className="w-3.5 h-3.5" />
                Dependencies ({dependencies.length})
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                {dependencies.map((dep, i) => (
                  <div key={i} className="text-xs flex gap-2 items-start">
                    <span className={`px-1.5 py-0.5 rounded shrink-0 ${
                      dep.severity === 'high' ? 'text-red-600 bg-red-100' : 'text-amber-600 bg-amber-100'
                    }`}>
                      {dep.severity}
                    </span>
                    <span className="text-slate-600">{dep.detail}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Validation Checks */}
          {validationChecks.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-600 uppercase tracking-wide">
                <ClipboardCheck className="w-3.5 h-3.5" />
                Validate ({validationChecks.length} checks)
              </div>
              <div className="space-y-2">
                {validationChecks.map((check, i) => (
                  <div key={i} className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs font-medium text-slate-700">{i + 1}. {check.name}</span>
                      {check.owner && (
                        <span className="text-xs text-slate-500">{check.owner}</span>
                      )}
                    </div>
                    <div className="space-y-1 text-xs">
                      <p className="text-slate-600"><span className="text-slate-500">Action:</span> {check.action}</p>
                      <p className="text-slate-600"><span className="text-emerald-600">Pass if:</span> {check.pass_threshold}</p>
                      <p className="text-slate-600"><span className="text-amber-600">If failed:</span> {check.if_failed}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Delete Button at Bottom */}
          {onDeleteCampaign && (
            <div className="pt-4 border-t border-slate-200">
              <Button 
                variant="outline"
                className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                onClick={() => onDeleteCampaign(campaignId, campaign?.name || 'this campaign')}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete from Bucket List
              </Button>
              <p className="text-xs text-slate-400 text-center mt-2">
                This won't affect your original Plan
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
